/**
 * Retry helper for calls to the central license server (central.arbi.work).
 *
 * Why this exists: the central server IP-rate-limits bursts of requests. In
 * CI, the ephemeral integration-test backend registers a batch of fresh test
 * users on every run, and each registration hits central's email-verification
 * endpoints (`/license-management/verify-ci` and, indirectly, the
 * `/v1/user/verify-email` proxy). Once the runner's IP crosses central's
 * limit, central responds with HTTP 429, which used to flake the whole
 * pipeline. We retry those transient failures with exponential backoff +
 * jitter so a brief rate-limit window doesn't fail the run.
 *
 * We retry ONLY on transient conditions — HTTP 429, HTTP 5xx, and
 * network/fetch errors. We never retry other 4xx responses (bad request,
 * auth, not-found): those are deterministic and won't change on retry.
 */

/** HTTP statuses worth retrying: rate-limit (429) and any server error (5xx). */
export function isRetryableStatus(status: number | undefined): boolean {
  if (status === undefined) return false
  return status === 429 || (status >= 500 && status <= 599)
}

export interface RetryOptions<T> {
  /** Decide whether an attempt should be retried, given its result or thrown error. */
  shouldRetry: (result: T | undefined, error: unknown) => boolean
  /** Max attempts, including the first. Default 6. */
  maxAttempts?: number
  /** Base delay in ms for the exponential schedule. Default 1000. */
  baseDelayMs?: number
  /** Upper bound for any single delay in ms. Default 20000. */
  maxDelayMs?: number
}

/**
 * Run `fn`, retrying transient failures with exponential backoff + jitter.
 *
 * Schedule (baseDelayMs=1000): ~1s, 2s, 4s, 8s, 16s, capped at maxDelayMs
 * (~20s), each with ±20% jitter to avoid synchronised retry storms.
 *
 * On success — or on a non-retryable result/error — the value is returned or
 * the error rethrown immediately. When retries are exhausted the LAST result
 * is returned (or the last error rethrown) so callers keep their existing
 * error-message/behaviour on the final failure.
 */
export async function withRetry<T>(fn: () => Promise<T>, options: RetryOptions<T>): Promise<T> {
  const { shouldRetry, maxAttempts = 6, baseDelayMs = 1000, maxDelayMs = 20_000 } = options

  let lastResult: T | undefined
  let lastError: unknown
  let lastAttemptThrew = false

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    let result: T | undefined
    let error: unknown
    let threw = false
    try {
      result = await fn()
      lastResult = result
      lastAttemptThrew = false
    } catch (err) {
      threw = true
      error = err
      lastError = err
      lastAttemptThrew = true
    }

    if (!shouldRetry(threw ? undefined : result, threw ? error : undefined)) {
      if (threw) throw error
      return result as T
    }

    if (attempt === maxAttempts - 1) break

    // Exponential backoff with ±20% jitter.
    const exp = Math.min(baseDelayMs * 2 ** attempt, maxDelayMs)
    const jitter = exp * 0.2 * (Math.random() * 2 - 1)
    const delay = Math.max(0, Math.round(exp + jitter))
    await new Promise((resolve) => setTimeout(resolve, delay))
  }

  // Retries exhausted — preserve the caller's final-failure behaviour.
  if (lastAttemptThrew) throw lastError
  return lastResult as T
}
